/**
 * Turns a rejected uploadImage() into a user-facing message that says
 * what actually went wrong, instead of one generic "check your
 * connection" for every failure. Duck-typed on the axios error shape
 * (response / request / code) so the package stays transport-agnostic.
 */
const GENERIC_MESSAGE =
	'Image upload failed. Please check your connection and try again.';

const sentence = (text: string) =>
	/[.!?]$/.test(text) ? text : `${text}.`;

export function describeUploadError(error: unknown): string {
	const err = (error || {}) as {
		response?: { status?: number; data?: any };
		request?: unknown;
		code?: string;
		message?: string;
	};

	if (err.response) {
		const { status, data } = err.response;
		const serverMessage =
			data && typeof data === 'object'
				? data.message || data.error
				: undefined;
		if (typeof serverMessage === 'string' && serverMessage.trim()) {
			return `Image upload failed (${status}): ${sentence(serverMessage.trim())}`;
		}
		return `Image upload failed: the server responded with status ${status}.`;
	}

	if (err.code === 'ECONNABORTED') {
		return 'Image upload failed: the upload timed out before the server answered.';
	}

	if (err.request) {
		return 'Image upload failed: the upload never reached the server. Something on this device or network blocked it (VPN, proxy, antivirus web protection or a browser extension), or the connection dropped.';
	}

	if (typeof err.message === 'string' && err.message.trim()) {
		return `Image upload failed: ${sentence(err.message.trim())}`;
	}

	return GENERIC_MESSAGE;
}
